/**
 * MillageOverrideModal - Modal for customizing millage rate projections
 *
 * Allows users to override specific year millage rates for fine-tuning.
 * Creates a new configuration with the overrides applied when submitted.
 */

import { Box, Button, Dialog, HStack, Input, Stack, Table, Text, VStack } from '@chakra-ui/react';
import { RefreshCw, X } from 'lucide-react';
import type React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  initializeMillageOverrides,
  removeMillageOverride,
  setMillageOverride,
  validateMillageOverride,
} from '~/services/override-service';
import type { MillageOverrideRow, MillageOverrides } from '~/types/overrides';

// ============================================================================
// Types
// ============================================================================

export interface MillageOverrideModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Handler called when modal should close */
  onClose: () => void;
  /** Handler called when user submits overrides */
  onSubmit: (overrides: MillageOverrides) => void;
  /** The current millage map (year -> rate) from the candidate's shape */
  millageMap: Record<number, number> | null;
  /** Source shape ID for tracking */
  sourceShapeId?: string;
  /** Source shape name for display */
  sourceShapeName?: string;
  /** Existing overrides to pre-populate (for editing) */
  existingOverrides?: MillageOverrides | null;
  /** Peak millage constraint (optional) */
  peakMillage?: number;
}

// ============================================================================
// Utility Functions
// ============================================================================

const formatMillage = (value: number): string => {
  return value.toFixed(4);
};

const formatPercentage = (value: number): string => {
  if (!Number.isFinite(value)) return '--';
  const sign = value >= 0 ? '+' : '';
  return `${sign}${value.toFixed(2)}%`;
};

const parseMillageInput = (input: string): number | null => {
  const cleaned = input.trim();
  const parsed = Number.parseFloat(cleaned);
  return Number.isNaN(parsed) ? null : parsed;
};

// ============================================================================
// Component
// ============================================================================

export const MillageOverrideModal: React.FC<MillageOverrideModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  millageMap,
  sourceShapeId,
  sourceShapeName,
  existingOverrides,
  peakMillage,
}) => {
  // State for the working overrides
  const [workingOverrides, setWorkingOverrides] = useState<MillageOverrides | null>(null);
  // State for currently editing cell
  const [editingYear, setEditingYear] = useState<number | null>(null);
  // State for edit input value
  const [editValue, setEditValue] = useState('');
  // State for validation error
  const [validationError, setValidationError] = useState<string | null>(null);
  // Ref for the input field to focus when row is clicked
  const inputRef = useRef<HTMLInputElement>(null);

  // Initialize state when modal opens
  // NOTE: Override values are ABSOLUTE (user enters 2.5 to get 2.5 mills), not relative.
  // The baseMillageMap is updated to the current candidate's millage map each time the
  // modal opens. This ensures the "Original Rate" column shows accurate values for
  // comparison, while the override rates themselves remain unchanged.
  useEffect(() => {
    if (isOpen && millageMap) {
      if (existingOverrides) {
        // Preserve existing override values but update baseMillageMap to current candidate
        setWorkingOverrides({
          byYear: { ...existingOverrides.byYear },
          baseMillageMap: { ...millageMap },
          sourceShapeId: existingOverrides.sourceShapeId,
          sourceShapeName: existingOverrides.sourceShapeName,
        });
      } else {
        // Initialize fresh overrides with the current millage map as the base
        setWorkingOverrides(initializeMillageOverrides(millageMap, sourceShapeId, sourceShapeName));
      }
      setEditingYear(null);
      setEditValue('');
      setValidationError(null);
    } else if (isOpen && !millageMap) {
      setWorkingOverrides(null);
    }
  }, [isOpen, existingOverrides, millageMap, sourceShapeId, sourceShapeName]);

  // Build table rows from millage map
  const tableRows: MillageOverrideRow[] = useMemo(() => {
    if (!millageMap) return [];

    return Object.entries(millageMap)
      .map(([yearStr, rate]) => {
        const year = Number.parseInt(yearStr, 10);
        const override = workingOverrides?.byYear[year];
        const currentRate = override ? override.overrideRate : rate;
        const percentChange = rate !== 0 ? ((currentRate - rate) / rate) * 100 : 0;

        return {
          taxYear: year,
          originalRate: rate,
          currentRate,
          isOverridden: !!override,
          estimatedRevenue: 0, // Could calculate if we had taxable value
          percentChange,
        };
      })
      .sort((a, b) => a.taxYear - b.taxYear);
  }, [millageMap, workingOverrides]);

  // Count of overridden values
  const overrideCount = useMemo(() => {
    return Object.keys(workingOverrides?.byYear ?? {}).length;
  }, [workingOverrides]);

  // Handle starting to edit a cell
  const handleStartEdit = useCallback((row: MillageOverrideRow) => {
    setEditingYear(row.taxYear);
    setEditValue(row.currentRate.toString());
    setValidationError(null);
    // Focus the input after state updates
    setTimeout(() => {
      inputRef.current?.focus();
      inputRef.current?.select();
    }, 0);
  }, []);

  // Handle row click (to start editing)
  const handleRowClick = useCallback(
    (row: MillageOverrideRow, e: React.MouseEvent) => {
      // Don't start editing if clicking on the reset button or if already editing
      if (
        editingYear === row.taxYear ||
        (e.target as HTMLElement).closest('button') ||
        (e.target as HTMLElement).tagName === 'BUTTON'
      ) {
        return;
      }
      handleStartEdit(row);
    },
    [editingYear, handleStartEdit]
  );

  // Handle input focus to select all text
  const handleInputFocus = useCallback((e: React.FocusEvent<HTMLInputElement>) => {
    e.target.select();
  }, []);

  // Handle confirming an edit
  const handleConfirmEdit = useCallback(() => {
    if (editingYear === null || !workingOverrides || !millageMap) return;

    const parsed = parseMillageInput(editValue);
    if (parsed === null) {
      setValidationError('Please enter a valid number');
      return;
    }

    const error = validateMillageOverride(parsed, peakMillage);
    if (error) {
      setValidationError(error);
      return;
    }

    // Find the original value
    const originalRate = millageMap[editingYear];
    if (originalRate === undefined) return;

    // If the value is the same as original, remove the override
    if (Math.abs(parsed - originalRate) < 0.0001) {
      setWorkingOverrides(removeMillageOverride(workingOverrides, editingYear));
    } else {
      // Set the override
      setWorkingOverrides(setMillageOverride(workingOverrides, editingYear, originalRate, parsed));
    }

    setEditingYear(null);
    setEditValue('');
    setValidationError(null);
  }, [editingYear, editValue, millageMap, workingOverrides, peakMillage]);

  // Handle canceling an edit
  const handleCancelEdit = useCallback(() => {
    setEditingYear(null);
    setEditValue('');
    setValidationError(null);
  }, []);

  // Handle key press in edit input
  const handleEditKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        handleConfirmEdit();
      } else if (e.key === 'Escape') {
        handleCancelEdit();
      }
    },
    [handleConfirmEdit, handleCancelEdit]
  );

  // Handle resetting a single row
  const handleResetRow = useCallback(
    (taxYear: number) => {
      if (workingOverrides) {
        setWorkingOverrides(removeMillageOverride(workingOverrides, taxYear));
      }
    },
    [workingOverrides]
  );

  // Handle resetting all overrides
  const handleResetAll = useCallback(() => {
    if (millageMap) {
      setWorkingOverrides(initializeMillageOverrides(millageMap, sourceShapeId, sourceShapeName));
    } else {
      setWorkingOverrides(null);
    }
  }, [millageMap, sourceShapeId, sourceShapeName]);

  // Handle submit
  const handleSubmit = useCallback(() => {
    if (!workingOverrides || Object.keys(workingOverrides.byYear).length === 0) {
      // No overrides to submit
      return;
    }

    onSubmit(workingOverrides);
    onClose();
  }, [workingOverrides, onSubmit, onClose]);

  // Handle cancel
  const handleCancel = useCallback(() => {
    onClose();
  }, [onClose]);

  return (
    <Dialog.Root
      open={isOpen}
      onOpenChange={(e) => (!e.open ? onClose() : undefined)}
      placement="center"
      size="xl"
    >
      <Dialog.Backdrop />
      <Dialog.Positioner>
        <Dialog.Content maxWidth="700px" maxHeight="80vh">
          <Dialog.Header>
            <Dialog.Title>Customize Millage Rates</Dialog.Title>
          </Dialog.Header>

          <Dialog.Body overflow="auto">
            <VStack align="stretch" gap={4}>
              {/* Info text */}
              <Box p={3} bg="blue.50" borderRadius="md" borderWidth="1px" borderColor="blue.200">
                <Text fontSize="sm" color="blue.800">
                  Click on any rate to edit it. Changes will create a new configuration with your
                  customized millage rates applied.
                  {sourceShapeName && (
                    <Text as="span" fontWeight="medium">
                      {' '}
                      Based on: {sourceShapeName}
                    </Text>
                  )}
                </Text>
              </Box>

              {/* Peak millage info if available */}
              {peakMillage && (
                <Box p={2} bg="gray.50" borderRadius="md">
                  <Text fontSize="xs" color="gray.600">
                    Peak millage constraint: {peakMillage.toFixed(4)} mills
                  </Text>
                </Box>
              )}

              {/* Validation error */}
              {validationError && (
                <Box p={3} bg="red.50" borderRadius="md" borderWidth="1px" borderColor="red.200">
                  <Text fontSize="sm" color="red.600">
                    {validationError}
                  </Text>
                </Box>
              )}

              {/* Table */}
              <Box borderWidth="1px" borderRadius="md" overflow="hidden">
                <Table.Root size="sm">
                  <Table.Header>
                    <Table.Row>
                      <Table.ColumnHeader>Tax Year</Table.ColumnHeader>
                      <Table.ColumnHeader textAlign="right">Original Rate</Table.ColumnHeader>
                      <Table.ColumnHeader textAlign="right">Custom Rate</Table.ColumnHeader>
                      <Table.ColumnHeader textAlign="right">Change</Table.ColumnHeader>
                      <Table.ColumnHeader width="60px" />
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    {tableRows.map((row) => (
                      <Table.Row
                        key={row.taxYear}
                        bg={row.isOverridden ? 'purple.50' : undefined}
                        borderLeftWidth={row.isOverridden ? '3px' : undefined}
                        borderLeftColor={row.isOverridden ? 'purple.400' : undefined}
                        cursor={editingYear === row.taxYear ? 'default' : 'pointer'}
                        onClick={(e) => handleRowClick(row, e)}
                        _hover={
                          editingYear !== row.taxYear
                            ? {
                                bg: row.isOverridden ? 'purple.200' : 'blue.200',
                              }
                            : undefined
                        }
                        transition="background-color 0.15s ease"
                      >
                        <Table.Cell fontWeight="medium">{row.taxYear}</Table.Cell>
                        <Table.Cell textAlign="right" color="gray.600">
                          {formatMillage(row.originalRate)}
                        </Table.Cell>
                        <Table.Cell textAlign="right">
                          <Box
                            display="inline-flex"
                            alignItems="center"
                            justifyContent="flex-end"
                            minHeight="32px"
                            width="120px"
                          >
                            {editingYear === row.taxYear ? (
                              <Input
                                ref={inputRef}
                                size="sm"
                                value={editValue}
                                onChange={(e) => setEditValue(e.target.value)}
                                onKeyDown={handleEditKeyDown}
                                onBlur={handleConfirmEdit}
                                onFocus={handleInputFocus}
                                autoFocus
                                textAlign="right"
                                width="100%"
                              />
                            ) : (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleStartEdit(row)}
                                fontWeight={row.isOverridden ? 'semibold' : 'normal'}
                                color={row.isOverridden ? 'purple.700' : undefined}
                                width="100%"
                                justifyContent="flex-end"
                              >
                                {formatMillage(row.currentRate)}
                              </Button>
                            )}
                          </Box>
                        </Table.Cell>
                        <Table.Cell
                          textAlign="right"
                          color={
                            row.percentChange > 0
                              ? 'green.600'
                              : row.percentChange < 0
                                ? 'red.600'
                                : 'gray.500'
                          }
                        >
                          {row.isOverridden ? formatPercentage(row.percentChange) : '--'}
                        </Table.Cell>
                        <Table.Cell>
                          {row.isOverridden && (
                            <Button
                              size="xs"
                              variant="ghost"
                              colorPalette="gray"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleResetRow(row.taxYear);
                              }}
                              title="Reset to original"
                            >
                              <X size={14} />
                            </Button>
                          )}
                        </Table.Cell>
                      </Table.Row>
                    ))}
                  </Table.Body>
                </Table.Root>
              </Box>

              {/* Override count */}
              <Text fontSize="sm" color="gray.600">
                {overrideCount === 0
                  ? 'No rates customized'
                  : `${overrideCount} rate${overrideCount > 1 ? 's' : ''} customized`}
              </Text>
            </VStack>
          </Dialog.Body>

          <Dialog.Footer>
            <Stack direction="row" gap={2} justify="space-between" width="100%">
              <Button
                variant="ghost"
                colorPalette="red"
                onClick={handleResetAll}
                disabled={overrideCount === 0}
              >
                <RefreshCw size={14} />
                <Text ml={1}>Reset All</Text>
              </Button>
              <HStack gap={2}>
                <Button variant="ghost" onClick={handleCancel}>
                  Cancel
                </Button>
                <Button colorPalette="purple" onClick={handleSubmit} disabled={overrideCount === 0}>
                  Run Configuration
                </Button>
              </HStack>
            </Stack>
          </Dialog.Footer>
        </Dialog.Content>
      </Dialog.Positioner>
    </Dialog.Root>
  );
};
